import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { Color, GameAction, Move, OnlineGameSnapshot, Square } from '@ccc/shared-types';
import { clockMsForDisplay, createEngine } from '@ccc/rule-engine';
import { modifiersForGame } from '@ccc/cards';
import { ChessBoard } from '../components/ChessBoard';
import { Panel } from '../components/Card';
import { CardTile } from '../components/CardTile';
import { Button } from '../components/Button';
import { NoScroll } from '../components/Layout';
import { cardById } from '../lib/localGame';
import { useOnlineGame } from '../lib/multiplayer';
import { EffectBlock, MoveHistory } from './Game';

export function OnlineGameRoute() {
  const gameId = window.location.pathname.split('/').filter(Boolean).at(-1) ?? '';
  const { snapshot, status, error, send } = useOnlineGame(gameId);
  const [flipped, setFlipped] = useState(false);
  const now = useNow();
  const state = snapshot?.state ?? null;
  const playerSide = snapshot?.playerSide ?? null;
  const role = snapshot?.role ?? 'spectator';

  const engine = useMemo(() => {
    if (!state?.activeRules.white || !state.activeRules.black) return null;
    return createEngine(
      modifiersForGame({
        whiteCard: state.activeRules.white,
        blackCard: state.activeRules.black,
      }),
    );
  }, [state?.activeRules.white, state?.activeRules.black]);

  const legalMovesFor = useMemo(() => {
    if (!state || !engine) return undefined;
    return (sq: Square): Move[] => {
      const piece = Object.values(state.pieces).find((p) => p.square === sq);
      if (!piece) return [];
      return engine.generateLegalMovesForPiece(piece, state);
    };
  }, [state, engine]);

  useEffect(() => {
    if (!state || state.status !== 'active') return;
    const ticking = state.clocks.ticking ?? state.turn;
    if (clockMsForDisplay(state, ticking, Date.now()) <= 0) send({ kind: 'claim_timeout' });
  }, [now, send, state]);

  if (!snapshot || !state) {
    return (
      <div className="h-full w-full grid place-items-center px-5">
        <NoScroll />
        <Panel className="p-6 text-center">
          <div className="font-display text-2xl font-bold">Loading game</div>
          <p className="mt-2 text-sm text-ink-muted">{error ?? 'Connecting to the table...'}</p>
          <Link to="/play" className="inline-block mt-4">
            <Button variant="secondary">Back to lobby</Button>
          </Link>
        </Panel>
      </div>
    );
  }

  if (state.status === 'awaiting_picks' && state.draft) {
    const offer =
      playerSide === 'black'
        ? state.draft.blackOffer
        : playerSide === 'white'
        ? state.draft.whiteOffer
        : [];
    const alreadyPicked =
      playerSide === 'black'
        ? state.draft.blackPicked
        : playerSide === 'white'
        ? state.draft.whitePicked
        : false;
    return (
      <div className="h-full w-full px-3 md:px-8 py-4 grid grid-rows-[auto_1fr] gap-4 overflow-hidden">
        <NoScroll />
        <GameHeader snapshot={snapshot} status={status} error={error} />
        <div className="min-h-0 grid place-items-center overflow-hidden">
          {role === 'spectator' ? (
            <Panel className="p-6 text-center">
              <div className="font-display text-2xl font-bold">Draft in progress</div>
              <p className="mt-2 text-sm text-ink-muted">Both players are choosing their rules.</p>
            </Panel>
          ) : (
            <div className="min-h-0 w-full max-w-[1100px] flex flex-col items-center overflow-hidden">
              <h1 className="font-display text-3xl font-bold">Pick your rule</h1>
              <p className="text-sm text-ink-muted">
                {alreadyPicked ? 'Waiting for the other player.' : 'Choose one card for this game.'}
              </p>
              <div className="mt-5 min-h-0 flex items-start justify-center gap-4 flex-wrap overflow-auto">
                {offer.map((cid) => {
                  const c = cardById(cid)!;
                  return (
                    <CardTile
                      key={cid}
                      card={c}
                      rarity={
                        c.category === 'general'
                          ? 'rare'
                          : c.category === 'king' || c.category === 'queen'
                          ? 'legendary'
                          : 'common'
                      }
                      selected={
                        snapshot.playerSide === 'white'
                          ? state.activeRules.white === cid
                          : state.activeRules.black === cid
                      }
                      onPick={() => !alreadyPicked && send({ kind: 'pick_card', cardId: cid })}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const orientation: Color =
    role === 'spectator' ? (flipped ? 'black' : 'white') : playerSide ?? 'white';
  const activeSide = state.pendingBonus?.side ?? state.turn;
  const playableSide =
    state.status === 'active' && status === 'online' && playerSide === activeSide ? playerSide : null;
  const whiteCard = state.activeRules.white ? cardById(state.activeRules.white) : null;
  const blackCard = state.activeRules.black ? cardById(state.activeRules.black) : null;
  const canStoreTempo = Boolean(
    playerSide &&
      engine?.generateLegalActions(state, playerSide).some((action) => action.kind === 'store_tempo'),
  );

  return (
    <div className="h-full w-full max-w-[1400px] mx-auto px-3 md:px-8 py-3 grid grid-rows-[auto_1fr] gap-3 overflow-hidden">
      <NoScroll />
      <GameHeader snapshot={snapshot} status={status} error={error} />
      <div className="min-h-0 grid lg:grid-cols-[1fr_minmax(0,min(640px,calc(100svh-11rem)))_1fr] gap-3 md:gap-5 overflow-hidden">
        <aside className="min-h-0 hidden lg:flex flex-col gap-3 justify-end overflow-hidden">
          <Panel className="p-4">
            <SeatPanel
              label={orientation === 'white' ? 'Opponent' : 'You'}
              name={snapshot.players.black.user?.displayName ?? 'Black'}
              card={blackCard}
              clockMs={clockMsForDisplay(state, 'black', now)}
              rating={snapshot.players.black.ratingAfter ?? snapshot.players.black.rating?.rating}
            />
          </Panel>
        </aside>

        <div className="min-h-0 grid place-items-center overflow-hidden">
          <div
            className="aspect-square"
            style={{ width: 'min(100%, calc(100svh - 11rem))' }}
          >
            <ChessBoard
              state={state}
              orientation={orientation}
              playableSide={playableSide}
              legalMovesFor={legalMovesFor}
              onMove={(move) => send({ kind: 'move', move })}
            />
          </div>
        </div>

        <aside className="min-h-0 flex flex-col gap-3 overflow-hidden">
          <Panel className="p-4 shrink-0">
            <SeatPanel
              label={orientation === 'white' ? 'You' : 'Opponent'}
              name={snapshot.players.white.user?.displayName ?? 'White'}
              card={whiteCard}
              clockMs={clockMsForDisplay(state, 'white', now)}
              rating={snapshot.players.white.ratingAfter ?? snapshot.players.white.rating?.rating}
            />
          </Panel>
          <Panel className="p-4 shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted">Move</div>
                <div className="font-display text-2xl font-bold">{state.moveNumber}</div>
              </div>
              <div className="text-right text-sm text-ink-muted">
                Turn <span className="capitalize text-ink font-semibold">{activeSide}</span>
                <br />
                {snapshot.rated ? 'Rated' : 'Casual'} · {snapshot.timeClass}
              </div>
            </div>
            <EffectBlock
              state={state}
              canStoreTempo={canStoreTempo}
              onStoreTempo={() => send({ kind: 'store_tempo' })}
            />
          </Panel>
          <Panel className="p-4 min-h-0 flex flex-col">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted shrink-0">Moves</h3>
            <div className="mt-2 min-h-0 flex-1 overflow-auto text-sm font-mono text-ink-muted">
              <MoveHistory state={state} />
            </div>
          </Panel>
          <div className="grid grid-cols-2 gap-2 shrink-0">
            {role === 'spectator' ? (
              <Button variant="secondary" onClick={() => setFlipped((v) => !v)}>
                Flip board
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => send({ kind: 'offer_draw' })}>
                  Offer draw
                </Button>
                {snapshot.drawOffer && snapshot.drawOffer !== playerSide ? (
                  <Button onClick={() => send({ kind: 'accept_draw' })}>Accept draw</Button>
                ) : (
                  <Button variant="secondary" onClick={() => send({ kind: 'resign' })}>
                    Resign
                  </Button>
                )}
              </>
            )}
          </div>
          {state.status === 'ended' && (
            <Panel className="p-4 ring-2 ring-accent shrink-0">
              <div className="font-display text-xl font-bold">Game over</div>
              <div className="text-sm text-ink-muted">
                {state.winner === 'draw' ? 'Drawn.' : `${state.winner} wins.`}
              </div>
              <a href={`/replays/${gameId}`} className="inline-block mt-3">
                <Button>Open replay</Button>
              </a>
            </Panel>
          )}
        </aside>
      </div>
    </div>
  );
}

function GameHeader({
  snapshot,
  status,
  error,
}: {
  snapshot: OnlineGameSnapshot;
  status: string;
  error: string | null;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-2xl md:text-3xl font-bold truncate">
          {snapshot.players.white.user?.displayName ?? 'White'} vs{' '}
          {snapshot.players.black.user?.displayName ?? 'Black'}
        </h1>
        <p className="text-xs md:text-sm text-ink-muted">
          {snapshot.role === 'spectator' ? 'Watching' : `Playing ${snapshot.role}`} · {status}
          {error ? ` · ${error}` : ''}
        </p>
      </div>
      <Link to="/play">
        <Button variant="secondary" size="sm">
          Lobby
        </Button>
      </Link>
    </div>
  );
}

function SeatPanel({
  label,
  name,
  card,
  clockMs,
  rating,
}: {
  label: string;
  name: string;
  card: ReturnType<typeof cardById> | null | undefined;
  clockMs: number;
  rating?: number | null | undefined;
}) {
  const seconds = Math.max(0, Math.floor(clockMs / 1000));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
        <div className="font-mono text-lg font-semibold tabular-nums">
          {Math.floor(seconds / 60)}:{(seconds % 60).toString().padStart(2, '0')}
        </div>
      </div>
      <div className="mt-1 font-semibold truncate">
        {name} {rating ? <span className="text-xs text-ink-muted">({Math.round(rating)})</span> : null}
      </div>
      {card ? (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{card.category}</div>
          <div className="font-display font-semibold">{card.shortTitle}</div>
          <p className="mt-1 text-xs text-ink-muted">{card.description}</p>
        </div>
      ) : (
        <div className="mt-3 text-xs text-ink-muted">No card selected.</div>
      )}
    </div>
  );
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);
  return now;
}
